import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { CREATE_WHY_YOU_LOVE_TEXT_MUTATION, MOMENT_CLAIM_MUTATION } from '@/organisms/TellWhyYouLoveThis/queries'
import { getWebClient } from '@/services/ApolloClient'
import { GET_MOMENT_BY_ID, GET_MOMENTS_BY_EPISODE_GUID } from '@/services/MomentsService/queries'
import { GET_COLLECTIBLE_ORDER_HISTORY, GET_USER_COLLECTIBLES } from '@/services/NftService'
import {
  CreateUserStoryTextInput,
  CreateUserStoryTextMutation,
  CreateUserStoryTextMutationVariables,
  GetCollectibleOrderHistoryQuery,
  GetCollectibleOrderHistoryQueryVariables,
  GetMomentsByEpisodeGuidQuery,
  GetMomentsByEpisodeGuidQueryVariables,
  MomentClaimMutation,
  MomentClaimMutationVariables,
} from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { MomentWithEpisode } from './types'

export const useGetMomentsByEpisode = (episodeGuid: string) => {
  const client = getWebClient()
  const { data } = useQuery<GetMomentsByEpisodeGuidQuery, GetMomentsByEpisodeGuidQueryVariables>(
    GET_MOMENTS_BY_EPISODE_GUID,
    { client, variables: { guid: episodeGuid }, skip: !episodeGuid, errorPolicy: 'all' },
  )

  return useMemo(() => ({ data }), [data])
}

export const useCreateWhyYouLoveThis = () => {
  const client = getWebClient()
  const [createWhyYouLoveMutation, { loading }] = useMutation<
    CreateUserStoryTextMutation,
    CreateUserStoryTextMutationVariables
  >(CREATE_WHY_YOU_LOVE_TEXT_MUTATION, {
    client,
    errorPolicy: 'all',
  })

  const createWhyYouLoveText = useCallback(
    async (toSave: CreateUserStoryTextInput) => {
      return createWhyYouLoveMutation({
        refetchQueries: [{ query: GET_MOMENT_BY_ID, variables: { id: toSave.momentId } }, 'getMomentById'],
        variables: {
          createUserStoryTextInput: toSave,
        },
        errorPolicy: 'all',
      })
    },
    [createWhyYouLoveMutation],
  )
  return useMemo(() => ({ createWhyYouLoveText, isSavingWhyYouLove: loading }), [createWhyYouLoveText, loading])
}

export const useCollectibleOrderHistory = () => {
  const client = getWebClient()
  const { data } = useQuery<GetCollectibleOrderHistoryQuery, GetCollectibleOrderHistoryQueryVariables>(
    GET_COLLECTIBLE_ORDER_HISTORY,
    { client, errorPolicy: 'all' },
  )
  return useMemo(() => ({ data }), [data])
}

export const useMomentClaim = () => {
  const [momentClaimMutation, { loading: momentClaimIsLoading }] = useMutation<
    MomentClaimMutation,
    MomentClaimMutationVariables
  >(MOMENT_CLAIM_MUTATION, {
    errorPolicy: 'all',
  })

  const momentClaim = useCallback(
    async (claimToken: string) => {
      return momentClaimMutation({
        refetchQueries: [{ query: GET_USER_COLLECTIBLES }, 'getUserCollectibles'],
        variables: {
          momentClaimInput: { claimToken },
        },
        errorPolicy: 'all',
      })
    },
    [momentClaimMutation],
  )
  return useMemo(() => ({ momentClaim, momentClaimIsLoading }), [momentClaim, momentClaimIsLoading])
}

export const useTitle = ({ moment }: { moment: MomentWithEpisode }): string => {
  const { t } = useTranslate('watch')
  if (moment.type === 'COMMON') {
    return t('checkOutCollectibleScene', 'Check out this Collectible Scene, "{{momentTitle}}"!', {
      momentTitle: moment?.title,
    })
  }

  if (moment.type === 'GOLDEN') {
    return t('checkOutGoldenScene', 'Check out this Golden Scene, "{{momentTitle}}"!', {
      momentTitle: moment?.title,
    })
  }

  if (moment.title) {
    return moment.title
  }

  return t('checkOutThisScene', 'Check out this scene!')
}
